import {
  Box,
  CircularProgress,
  Divider,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import WSButton from "../../Components/Button/WSButton";

import { callAPI, Method } from "../../commonFunctions/CommonFunctions";
import { useSnackbar } from "../../Components/Snackbar/SnackBarContext";
import { NotFound } from "../NotFound";

const QPrint: React.FC = () => {
  const { id } = useParams();
  const contentRef = useRef<HTMLDivElement>(null);
  const { showMessage } = useSnackbar();

  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const paymentStages = [
    { description: "Approximate Estimation", percentage: null },
    { description: "Advance 30%", percentage: 30 },
    { description: "Unload Time 60%", percentage: 60 },
    { description: "After Completion 10%", percentage: 10 }
  ];

  useEffect(() => {
    async function getDataById() {
      try {
        const response: any = await callAPI("quotations", Method.GET, null, id);
        if (response?.data) {
          setData(response.data);
        }
      } catch (error) {
        showMessage("Something went wrong. Please try again.", "error");
      } finally {
        setLoading(false);
      }
    }

    if (id) {
      getDataById();
    }
  }, [id]);

  // 🖨️ Print Handler
  const handlePrint = useReactToPrint({
    contentRef
  });

  if (loading)
    return (
      <Box display="flex" justifyContent="center" mt={4}>
        <CircularProgress />
      </Box>
    );
  if (!data) return <NotFound />;

  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      p={4}
      bgcolor="grey.100"
      minHeight="100vh"
    >
      <WSButton
        id="printBtn"
        size="small"
        label="Print Quotation"
        onClick={handlePrint}
      />
      <Box
        ref={contentRef}
        mt={2}
        sx={{ p: 4, width: "100%", maxWidth: "800px", bgcolor: "white" }}
      >
        {/* Company Header */}
        <Box textAlign="center" borderBottom={1} pb={2} mb={2}>
          <Typography variant="h4" fontWeight="bold" color="error">
            MHB INTERLOCK
          </Typography>
          <Typography variant="subtitle1">Make Home Beautiful</Typography>
          <Typography variant="body2">
            E.mail: mhbinterlock@gmail.com | mhb-Industries
          </Typography>
          <Typography variant="body2">
            NADUKANI, PALLIVAYAL P.O 670142
          </Typography>
          <Typography variant="body2">PH: 9747535535, 9544541114/17</Typography>
        </Box>

        {/* Estimate Details */}
        <Box textAlign="left" mb={3}>
          <Typography variant="h6" color="primary">
            To,
          </Typography>
          <Typography>{data.customerName}</Typography>
          <Typography>Site at {data.address}.</Typography>
        </Box>

        {/* Estimation Details */}
        <Box textAlign="center" mb={3}>
          <Typography variant="h5">Quotation</Typography>
          <Typography variant="body2">
            Valid Till: {new Date(data.validTill).toLocaleDateString()}
          </Typography>
        </Box>

        <Box mb={3}>
          <Typography variant="h6">Dear sir/madam</Typography>
          <Typography variant="body2">
            {data.subject}
          </Typography>
        </Box>

        {/* Table */}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "grey.300" }}>
                <TableCell>Description</TableCell>
                <TableCell align="center">Qty</TableCell>
                <TableCell align="center">Unit</TableCell>
                <TableCell align="center">Unit Price</TableCell>
                <TableCell align="center">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {data.quotationItems.map((item: any) => (
                <TableRow key={item.id}>
                  <TableCell>{item.productName}</TableCell>
                  <TableCell align="center">{item.quantity}</TableCell>
                  <TableCell align="center">{item.unit}</TableCell>
                  <TableCell align="center">{item.unitPrice}</TableCell>
                  <TableCell align="center">{item.totalPrice}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Terms and Condtion */}

        <Box mt={2}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
            Terms and Conditions.
          </Typography>
          <Typography variant="body1" sx={{ pl: 2 }}>
            ➤ {data.termsAndConditions}
          </Typography>
        </Box>

        {/* Payment Terms */}
        <Box mt={4}>
          <Typography variant="h6" color="primary">
            Mode of Payment:
          </Typography>
          <ul>
            <li>
              <Typography>
                <b>30%</b> of total value of work on order confirmation.
              </Typography>
            </li>
            <li>
              <Typography>
                <b>60%</b> of total value of work on delivery of material to
                site.
              </Typography>
            </li>
            <li>
              <Typography>
                <b>10%</b> balance after completion of work.
              </Typography>
            </li>
          </ul>
        </Box>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="flex-end"
        >
          <Box>
            <Typography fontWeight="bold">Accepted:</Typography>
            <Typography>Name of Client: ___________________</Typography>
            <Typography>Witnesses:</Typography>
            <Typography>1. ___________________</Typography>
            <Typography>2. ___________________</Typography>
          </Box>
          <Box textAlign="right">
            <Typography>Thanks & Regards,</Typography>
            <Typography fontWeight="bold">JOSEPH PU</Typography>
            <Typography>Managing Director</Typography>
          </Box>
        </Box>

        {/* Payment Schedule */}
        <Typography
          variant="h6"
          textAlign="center"
          sx={{ mb: 2, mt: 3, fontWeight: 600, pageBreakBefore: "always" }}
        >
          Payment Schedule
        </Typography>

        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, boxShadow: 2 }}
        >
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: "primary.light" }}>
                <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Amount
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Percentage
                </TableCell>
                <TableCell align="center" sx={{ fontWeight: 600 }}>
                  Payable
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {paymentStages.map(({ description, percentage }, index) => {
                const payable = percentage
                  ? (data.grandTotal * percentage) / 100
                  : null;

                return (
                  <TableRow
                    key={index}
                    sx={{ bgcolor: index % 2 ? "grey.100" : "white" }}
                  >
                    <TableCell>{description}</TableCell>
                    <TableCell align="center">
                      {data.grandTotal.toLocaleString()}
                    </TableCell>
                    <TableCell align="center">
                      {percentage ? `${percentage}%` : "-"}
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>
                      {payable ? payable.toLocaleString() : "-"}
                    </TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>

        <Typography
          variant="body2"
          color="error"
          sx={{ mt: 2, textAlign: "center" }}
        >
          *All payments must be made through the provided bank account or Google
          Pay.*
        </Typography>

        {/* Bank Details */}
        <Typography
          variant="h6"
          textAlign="center"
          sx={{ mt: 3, mb: 2, fontWeight: 600 }}
        >
          Bank Details
        </Typography>

        <TableContainer
          component={Paper}
          sx={{ borderRadius: 2, boxShadow: 2 }}
        >
          <Table size="small">
            <TableBody>
              {[
                ["Account Holder", "Joseph P J"],
                ["Bank Name", "Federal Bank"],
                ["A/c No.", "13280100049462"],
                ["Branch", "Thalipparamba"],
                ["IFSC", "FDRL0001328"],
                ["G Pay No.", "97 47 535 535 (MHB JOSEPH)"]
              ].map(([label, value], index) => (
                <TableRow
                  key={index}
                  sx={{ bgcolor: index % 2 ? "grey.100" : "white" }}
                >
                  <TableCell sx={{ fontWeight: 500 }}>{label}:</TableCell>
                  <TableCell align="right">{value}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        {/* Footer */}
        <Divider sx={{ my: 3 }} />
        <Typography variant="h6" textAlign="center">
          Thanks & Regards,
        </Typography>
        <Typography textAlign="center" fontWeight="bold">
          Joseph PJ
        </Typography>
        <Typography textAlign="center">MHB Interlock</Typography>
      </Box>
    </Box>
  );
};

export default QPrint;
